import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { WerklijnContextMenuDetails } from '@/components/contextMenu/mwdContextMenu/types';
import { getMutatieErrorMessage, isMutatieSucces } from '@/queries/mutaties/dialogMutatieResponseUtils';
import { VerzwakOpdracht } from '@/queries/mutaties/opdrachten';
import { useMutaties } from '@/queries/mutaties/useMutaties';
import {
  ActiviteitVoorBewegingModel,
  mapArrayOfActiviteitVoorBeweging,
} from '@/queries/queryModels/activiteitenVoorBewegingModel';
import { useGetQuery } from '@/queries/useGetQuery';
import useClientState from '@/state/useClientState';

export type VerzwakInputs = {
  van: ActiviteitVoorBewegingModel | null | undefined;
  tot: ActiviteitVoorBewegingModel | null | undefined;
};

export const useVerzwakDialog = (details: WerklijnContextMenuDetails, handleOpenChange: (open: boolean) => void) => {
  const { verzwak } = useMutaties();
  const werklijnen = useClientState((state) => state.werklijnen);
  const lockedWerklijnVersie = details.werklijnVersie;
  const werklijnVersieFromState = details.werklijnId && werklijnen[details.werklijnId]?.versie;
  const [warningMessage, setWarningMessage] = useState<boolean>(false);
  const { handleSubmit, control, reset, setValue, clearErrors, watch } = useForm<VerzwakInputs>({
    mode: 'onSubmit',
  });
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (werklijnVersieFromState && lockedWerklijnVersie !== werklijnVersieFromState) {
      setWarningMessage(true);
    } else {
      setWarningMessage(false);
    }
  }, [lockedWerklijnVersie, werklijnVersieFromState]);

  // get activiteiten voor beweging
  const {
    data: activiteitenVoorBewegingData,
    fetchData: fetchActiviteitenVoorBewegingData,
    clearData: clearActiviteitenVoorBewegingData,
  } = useGetQuery(mapArrayOfActiviteitVoorBeweging);

  // reset all values
  const resetDialog = (closeButtonCLick: boolean) => {
    if (closeButtonCLick) {
      handleOpenChange(false);
    }
    reset();
    clearActiviteitenVoorBewegingData();
  };

  const onSubmit = async (target: VerzwakInputs) => {
    const verzwakOpdracht: VerzwakOpdracht = {
      betrokkenWerklijn: {
        id: details.werklijnId,
        versie: werklijnVersieFromState || details.werklijnVersie,
      },
      teVerzwakkenInzet: details.wleId ?? '',
      teVerzwakkenVertrek: {
        actueelGeplandeTijd: target.van?.actueelGeplandeTijd,
        oorspronkelijkGeplandeTijd: target.van?.oorspronkelijkGeplandeTijd,
        beweging: {
          treinDatum: details.treindatum,
          treinnummer: Number.parseInt(details?.treinnummer ?? ''),
        },
        dienstregelpunt: target.van?.dienstregelpunt,
        soort: target.van?.soort,
      },
      teVerzwakkenAankomst: {
        actueelGeplandeTijd: target.tot?.actueelGeplandeTijd,
        oorspronkelijkGeplandeTijd: target.tot?.oorspronkelijkGeplandeTijd,
        beweging: {
          treinDatum: details.treindatum,
          treinnummer: Number.parseInt(details?.treinnummer ?? ''),
        },
        dienstregelpunt: target.tot?.dienstregelpunt,
        soort: target.tot?.soort,
      },
    };
    await (async () => {
      const mutatieResponse = await verzwak(verzwakOpdracht);
      if (isMutatieSucces(mutatieResponse)) {
        resetDialog(true);
        setErrorMessage('');
      } else {
        const errorMessage = getMutatieErrorMessage(mutatieResponse);
        if (errorMessage) {
          setErrorMessage(errorMessage);
        }
      }
    })();
  };

  return {
    handleSubmit,
    clearErrors,
    setValue,
    control,
    onSubmit,
    resetDialog,
    fetchActiviteitenVoorBewegingData,
    activiteitenVoorBewegingData,
    watch,
    errorMessage,
    warningMessage,
  };
};
